/**
 * Database Query Helpers
 *
 * Provides tenant-scoped query functions for all tables.
 * Column names match the actual Supabase schema exactly (see ./database.types.ts).
 *
 * Usage:
 *   import { db } from './db';
 *   const { data, error } = await db.workflows.list(tenantId);
 *
 * Type safety: the supabase client is typed with `Database` so all `.from('x').select()`
 * calls infer Row / Insert / Update shapes from the live schema.
 *
 * Validation: Zod schemas at the most user-input-driven boundaries (registrations,
 * invitations, JML rules, approval requests). Internal partial updates rely on the
 * generated TS types only.
 */
import { z } from 'zod';
import { supabase } from './supabase';
import type { Insert, Row, Update } from './db.types';

const nowIso = (): string => new Date().toISOString();

// ─── Workflow Definitions ──────────────────────────────────

export const workflows = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('workflow_definitions')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    return { data: data ?? [], error };
  },

  async get(id: string) {
    const { data, error } = await supabase
      .from('workflow_definitions')
      .select('*')
      .eq('id', id)
      .single();
    return { data, error };
  },

  async create(workflow: Insert<'workflow_definitions'>) {
    const { data, error } = await supabase
      .from('workflow_definitions')
      .insert(workflow)
      .select()
      .single();
    return { data, error };
  },

  async update(id: string, updates: Update<'workflow_definitions'>) {
    const { data, error } = await supabase
      .from('workflow_definitions')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('workflow_definitions').delete().eq('id', id);
    return { error };
  },
};

// ─── JML Rules ─────────────────────────────────────────────

const JmlRuleInputSchema = z.object({
  tenant_id: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  rule_type: z.string().min(1),
  trigger_conditions: z.unknown().optional(),
  actions: z.unknown().optional(),
  is_enabled: z.boolean().optional(),
  created_by: z.string().uuid().nullable().optional(),
});

export const jmlRules = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('jml_rules')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    return { data: data ?? [], error };
  },

  async create(rule: Insert<'jml_rules'>) {
    JmlRuleInputSchema.parse(rule);
    const { data, error } = await supabase.from('jml_rules').insert(rule).select().single();
    return { data, error };
  },

  async update(id: string, updates: Update<'jml_rules'>) {
    const { data, error } = await supabase
      .from('jml_rules')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async toggle(id: string, currentEnabled: boolean) {
    const { data, error } = await supabase
      .from('jml_rules')
      .update({ is_enabled: !currentEnabled, updated_at: nowIso() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('jml_rules').delete().eq('id', id);
    return { error };
  },
};

// ─── JML Runs ──────────────────────────────────────────────

export const jmlRuns = {
  async list(tenantId: string, limit = 20) {
    const { data, error } = await supabase
      .from('jml_runs')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data ?? [], error };
  },

  async get(id: string) {
    const { data, error } = await supabase.from('jml_runs').select('*').eq('id', id).single();
    return { data, error };
  },

  async create(run: Insert<'jml_runs'>) {
    const { data, error } = await supabase.from('jml_runs').insert(run).select().single();
    return { data, error };
  },

  async update(id: string, updates: Update<'jml_runs'>) {
    const { data, error } = await supabase
      .from('jml_runs')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },
};

// ─── JML Schedules ─────────────────────────────────────────

export const jmlSchedules = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('jml_schedules')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    return { data: data ?? [], error };
  },

  /** Fetch the (typically single) schedule for a tenant. */
  async get(tenantId: string) {
    const { data, error } = await supabase
      .from('jml_schedules')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    return { data, error };
  },

  async create(schedule: Insert<'jml_schedules'>) {
    const { data, error } = await supabase.from('jml_schedules').insert(schedule).select().single();
    return { data, error };
  },

  /**
   * Upsert by tenant_id. Requires a UNIQUE constraint on jml_schedules.tenant_id;
   * if the schema doesn't have one, this falls back to insert-or-update by id.
   */
  async upsert(schedule: Insert<'jml_schedules'>) {
    const { data, error } = await supabase
      .from('jml_schedules')
      .upsert({ ...schedule, updated_at: nowIso() }, { onConflict: 'tenant_id' })
      .select()
      .single();
    return { data, error };
  },

  async update(id: string, updates: Update<'jml_schedules'>) {
    const { data, error } = await supabase
      .from('jml_schedules')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('jml_schedules').delete().eq('id', id);
    return { error };
  },
};

// ─── Bundles ───────────────────────────────────────────────

export const bundles = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('bundles')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    return { data: data ?? [], error };
  },

  async get(id: string) {
    const { data, error } = await supabase.from('bundles').select('*').eq('id', id).single();
    return { data, error };
  },

  async create(bundle: Insert<'bundles'>) {
    const { data, error } = await supabase.from('bundles').insert(bundle).select().single();
    return { data, error };
  },

  async update(id: string, updates: Update<'bundles'>) {
    const { data, error } = await supabase
      .from('bundles')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('bundles').delete().eq('id', id);
    return { error };
  },
};

// ─── Audit Logs ────────────────────────────────────────────

export const auditLogs = {
  async list(tenantId: string, limit = 50) {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data ?? [], error };
  },

  async create(entry: Insert<'audit_logs'>) {
    const { data, error } = await supabase.from('audit_logs').insert(entry).select().single();
    return { data, error };
  },
};

// ─── HR Employees ──────────────────────────────────────────

export interface HrEmployeeFilters {
  search?: string;
  status?: 'active' | 'inactive' | 'leaving' | string;
  page?: number;
  pageSize?: number;
}

export const hrEmployees = {
  async list(tenantId: string, filters: HrEmployeeFilters = {}) {
    let query = supabase.from('hr_employees').select('*').eq('tenant_id', tenantId);

    if (filters.search) {
      const search = filters.search;
      query = query.or(
        `first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%`
      );
    }
    if (filters.status === 'active') query = query.eq('employment_status', 'active');
    if (filters.status === 'inactive') query = query.eq('employment_status', 'inactive');

    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 25;
    const { data, error } = await query
      .order('last_name')
      .range((page - 1) * pageSize, page * pageSize - 1);
    return { data: data ?? [], error };
  },

  async listAll(tenantId: string) {
    const { data, error } = await supabase
      .from('hr_employees')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('last_name');
    return { data: data ?? [], error };
  },

  async get(id: string) {
    const { data, error } = await supabase.from('hr_employees').select('*').eq('id', id).single();
    return { data, error };
  },

  async create(employee: Insert<'hr_employees'>) {
    const { data, error } = await supabase.from('hr_employees').insert(employee).select().single();
    return { data, error };
  },

  async update(id: string, updates: Update<'hr_employees'>) {
    const { data, error } = await supabase
      .from('hr_employees')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('hr_employees').delete().eq('id', id);
    return { error };
  },

  async clearAll(tenantId: string) {
    const { error } = await supabase.from('hr_employees').delete().eq('tenant_id', tenantId);
    return { error };
  },
};

// ─── M365 Users (cached from Graph API sync) ──────────────

export interface M365UserFilters {
  search?: string;
  status?: 'active' | 'disabled' | string;
  page?: number;
  pageSize?: number;
}

export const m365Users = {
  async list(tenantId: string, filters: M365UserFilters = {}) {
    let query = supabase.from('m365_users').select('*').eq('tenant_id', tenantId);

    if (filters.search) {
      const search = filters.search;
      query = query.or(
        `display_name.ilike.%${search}%,mail.ilike.%${search}%,user_principal_name.ilike.%${search}%`
      );
    }
    if (filters.status === 'active') query = query.eq('account_enabled', true);
    if (filters.status === 'disabled') query = query.eq('account_enabled', false);

    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 25;
    const { data, error } = await query
      .order('display_name')
      .range((page - 1) * pageSize, page * pageSize - 1);
    return { data: data ?? [], error };
  },

  async listAll(tenantId: string) {
    const { data, error } = await supabase
      .from('m365_users')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('display_name');
    return { data: data ?? [], error };
  },

  async get(id: string) {
    const { data, error } = await supabase.from('m365_users').select('*').eq('id', id).single();
    return { data, error };
  },

  async update(id: string, updates: Update<'m365_users'>) {
    const { data, error } = await supabase
      .from('m365_users')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('m365_users').delete().eq('id', id);
    return { error };
  },

  async clearAll(tenantId: string) {
    const { error } = await supabase.from('m365_users').delete().eq('tenant_id', tenantId);
    return { error };
  },
};

// ─── Azure AD Configs ──────────────────────────────────────

export const azureAdConfigs = {
  async get(tenantId: string) {
    const { data, error } = await supabase
      .from('azure_ad_configs')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();
    return { data, error };
  },

  async upsert(config: Insert<'azure_ad_configs'>) {
    const { data, error } = await supabase
      .from('azure_ad_configs')
      .upsert({ ...config, updated_at: nowIso() }, { onConflict: 'tenant_id' })
      .select()
      .single();
    return { data, error };
  },

  async delete(tenantId: string) {
    const { error } = await supabase.from('azure_ad_configs').delete().eq('tenant_id', tenantId);
    return { error };
  },
};

// ─── HR Provider Configs ───────────────────────────────────

export const hrProviderConfigs = {
  async get(tenantId: string) {
    const { data, error } = await supabase
      .from('hr_provider_configs')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();
    return { data, error };
  },

  async upsert(config: Insert<'hr_provider_configs'>) {
    const { data, error } = await supabase
      .from('hr_provider_configs')
      .upsert({ ...config, updated_at: nowIso() }, { onConflict: 'tenant_id' })
      .select()
      .single();
    return { data, error };
  },
};

// ─── Roles ─────────────────────────────────────────────────

export const roles = {
  async list(tenantId?: string | null) {
    let query = supabase.from('roles').select('*');
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const { data, error } = await query.order('name');
    return { data: data ?? [], error };
  },

  async create(role: Insert<'roles'>) {
    const { data, error } = await supabase.from('roles').insert(role).select().single();
    return { data, error };
  },

  async update(id: string, updates: Update<'roles'>) {
    const { data, error } = await supabase
      .from('roles')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('roles').delete().eq('id', id);
    return { error };
  },
};

// ─── Users (Tenant-scoped) ─────────────────────────────────

export const users = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('email');
    return { data: data ?? [], error };
  },

  async get(userId: string) {
    const { data, error } = await supabase.from('users').select('*').eq('id', userId).single();
    return { data, error };
  },

  async update(id: string, updates: Update<'users'>) {
    const { data, error } = await supabase
      .from('users')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select();
    return { data: data?.[0] ?? null, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('users').delete().eq('id', id);
    return { error };
  },
};

// ─── Invitations ───────────────────────────────────────────

const InvitationInputSchema = z.object({
  tenant_id: z.string().uuid(),
  email: z.string().email(),
  role_id: z.string().min(1),
  invited_by: z.string().uuid().nullable().optional(),
  invitation_token: z.string().min(8),
  status: z.string().optional(),
  expires_at: z.string().optional(),
});

export const invitations = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('invitations')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    return { data: data ?? [], error };
  },

  async create(invitation: Insert<'invitations'>) {
    InvitationInputSchema.parse(invitation);
    const { data, error } = await supabase.from('invitations').insert(invitation).select().single();
    return { data, error };
  },

  async get(token: string) {
    const { data, error } = await supabase
      .from('invitations')
      .select('*')
      .eq('invitation_token', token)
      .single();
    return { data, error };
  },

  async update(id: string, updates: Update<'invitations'>) {
    const { data, error } = await supabase
      .from('invitations')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('invitations').delete().eq('id', id);
    return { error };
  },
};

// ─── Pending Registrations ─────────────────────────────────

const PendingRegistrationInputSchema = z.object({
  email: z.string().email(),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  company_name: z.string().min(1),
  password_hash: z.string().min(1),
  status: z.string().optional(),
});

export type PendingRegistrationStatus = 'pending' | 'approved' | 'rejected';

export const pendingRegistrations = {
  async create(registration: Insert<'pending_registrations'>) {
    PendingRegistrationInputSchema.parse(registration);
    const { data, error } = await supabase
      .from('pending_registrations')
      .insert(registration)
      .select()
      .single();
    return { data, error };
  },

  async getByEmail(email: string) {
    const { data, error } = await supabase
      .from('pending_registrations')
      .select('*')
      .eq('email', email)
      .maybeSingle();
    return { data, error };
  },

  async list(status: PendingRegistrationStatus | null = null) {
    let query = supabase.from('pending_registrations').select('*');
    if (status) query = query.eq('status', status);
    const { data, error } = await query.order('created_at', { ascending: false });
    return { data: data ?? [], error };
  },

  async updateStatus(id: string, status: PendingRegistrationStatus, userId: string | null = null) {
    const updates: Update<'pending_registrations'> = { status };
    if (status === 'approved') {
      updates.approved_by = userId;
      updates.approved_at = nowIso();
    } else if (status === 'rejected') {
      updates.rejected_by = userId;
      updates.rejected_at = nowIso();
    }
    const { data, error } = await supabase
      .from('pending_registrations')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },
};

// ─── Tenants ───────────────────────────────────────────────

export const tenants = {
  async list(limit = 100) {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data ?? [], error };
  },

  async get(id: string) {
    const { data, error } = await supabase
      .from('tenants')
      .select('*, tenant_settings(*)')
      .eq('id', id)
      .single();
    return { data, error };
  },

  async update(id: string, updates: Update<'tenants'>) {
    const { data, error } = await supabase
      .from('tenants')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },
};

// ─── Tenant Settings ───────────────────────────────────────

export const tenantSettings = {
  async get(tenantId: string) {
    const { data, error } = await supabase
      .from('tenant_settings')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();
    return { data, error };
  },

  async upsert(settings: Insert<'tenant_settings'>) {
    const { data, error } = await supabase
      .from('tenant_settings')
      .upsert({ ...settings, updated_at: nowIso() }, { onConflict: 'tenant_id' })
      .select()
      .single();
    return { data, error };
  },
};

// ─── Settings ──────────────────────────────────────────────

export const settings = {
  async get(tenantId: string) {
    const { data, error } = await supabase
      .from('settings')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();
    return { data, error };
  },

  async upsert(settingsData: Insert<'settings'>) {
    const { data, error } = await supabase
      .from('settings')
      .upsert({ ...settingsData, updated_at: nowIso() }, { onConflict: 'tenant_id' })
      .select()
      .single();
    return { data, error };
  },
};

// ─── Billing Plans ─────────────────────────────────────────

export const billingPlans = {
  async list() {
    const { data, error } = await supabase.from('billing_plans').select('*').order('sort_order');
    return { data: data ?? [], error };
  },

  async create(plan: Insert<'billing_plans'>) {
    const { data, error } = await supabase.from('billing_plans').insert(plan).select().single();
    return { data, error };
  },

  async update(id: string, updates: Update<'billing_plans'>) {
    const { data, error } = await supabase
      .from('billing_plans')
      .update({ ...updates, updated_at: nowIso() })
      .eq('id', id)
      .select();
    return { data: data?.[0] ?? null, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('billing_plans').delete().eq('id', id);
    return { error };
  },
};

// ─── Subscriptions ─────────────────────────────────────────

export const subscriptions = {
  async getByTenant(tenantId: string) {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*, billing_plans(*)')
      .eq('tenant_id', tenantId)
      .maybeSingle();
    return { data, error };
  },

  async list() {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*, billing_plans(*), tenants(*)');
    return { data: data ?? [], error };
  },

  async upsert(subscription: Insert<'subscriptions'>) {
    const { data, error } = await supabase
      .from('subscriptions')
      .upsert({ ...subscription, updated_at: nowIso() }, { onConflict: 'tenant_id' })
      .select()
      .single();
    return { data, error };
  },
};

// ─── Leaver Approval Requests ──────────────────────────────

const LeaverApprovalInputSchema = z
  .object({
    tenant_id: z.string().uuid(),
  })
  .passthrough();

export const leaverApprovalRequests = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('leaver_approval_requests')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    return { data: data ?? [], error };
  },

  async create(request: Insert<'leaver_approval_requests'>) {
    LeaverApprovalInputSchema.parse(request);
    const { data, error } = await supabase
      .from('leaver_approval_requests')
      .insert(request)
      .select()
      .single();
    return { data, error };
  },
};

// ─── Scheduled Offboardings ────────────────────────────────

export const scheduledOffboardings = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('scheduled_offboardings')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('scheduled_date');
    return { data: data ?? [], error };
  },

  async create(offboarding: Insert<'scheduled_offboardings'>) {
    const { data, error } = await supabase
      .from('scheduled_offboardings')
      .insert(offboarding)
      .select()
      .single();
    return { data, error };
  },
};

// ─── Usage Tracking ────────────────────────────────────────

export const usageTracking = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('usage_tracking')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    return { data: data ?? [], error };
  },
};

// ─── Employment Types ──────────────────────────────────────

export const employmentTypes = {
  async list(tenantId?: string | null) {
    let query = supabase.from('employment_types').select('*');
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const { data, error } = await query.order('sort_order');
    return { data: data ?? [], error };
  },
};

// ─── User Types ────────────────────────────────────────────

export const userTypes = {
  async list(tenantId?: string | null) {
    let query = supabase.from('user_types').select('*');
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const { data, error } = await query.order('sort_order');
    return { data: data ?? [], error };
  },
};

// ─── Password Reset Tokens (legacy) ────────────────────────
// Note: With Supabase Auth, password resets are handled natively. Kept for legacy compatibility.

export const passwordResetTokens = {
  async create(tokenData: Insert<'password_reset_tokens'>) {
    const { data, error } = await supabase
      .from('password_reset_tokens')
      .insert(tokenData)
      .select()
      .single();
    return { data, error };
  },

  async getByToken(token: string) {
    const { data, error } = await supabase
      .from('password_reset_tokens')
      .select('*')
      .eq('token', token)
      .maybeSingle();
    return { data, error };
  },
};

// ─── Admin Queries (Cross-Tenant) ──────────────────────────

export interface PlatformMetrics {
  totalUsers: number;
  totalWorkflows: number;
  totalRuns: number;
  totalBundles: number;
  totalRules: number;
}

export const adminQueries = {
  async platformMetrics(): Promise<PlatformMetrics> {
    const [usersRes, workflowsRes, runsRes, bundlesRes, rulesRes] = await Promise.all([
      supabase.from('users').select('id', { count: 'exact', head: true }),
      supabase.from('workflow_definitions').select('id', { count: 'exact', head: true }),
      supabase.from('jml_runs').select('id', { count: 'exact', head: true }),
      supabase.from('bundles').select('id', { count: 'exact', head: true }),
      supabase.from('jml_rules').select('id', { count: 'exact', head: true }),
    ]);
    return {
      totalUsers: usersRes.count ?? 0,
      totalWorkflows: workflowsRes.count ?? 0,
      totalRuns: runsRes.count ?? 0,
      totalBundles: bundlesRes.count ?? 0,
      totalRules: rulesRes.count ?? 0,
    };
  },

  async recentActivity(limit = 30) {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data ?? [], error };
  },

  async tenantDetails(tenantId: string) {
    const [usersRes, workflowsRes, runsRes, bundlesRes, rulesRes, azureRes, hrRes, subsRes] =
      await Promise.all([
        supabase
          .from('users')
          .select('id, email, role_id, is_active, created_at', { count: 'exact' })
          .eq('tenant_id', tenantId),
        supabase
          .from('workflow_definitions')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', tenantId),
        supabase
          .from('jml_runs')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', tenantId),
        supabase
          .from('bundles')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', tenantId),
        supabase
          .from('jml_rules')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', tenantId),
        supabase
          .from('azure_ad_configs')
          .select('is_configured, last_test_status')
          .eq('tenant_id', tenantId)
          .maybeSingle(),
        supabase
          .from('hr_provider_configs')
          .select('provider, is_active, last_sync_status')
          .eq('tenant_id', tenantId)
          .maybeSingle(),
        supabase
          .from('subscriptions')
          .select('*, billing_plans(*)')
          .eq('tenant_id', tenantId)
          .maybeSingle(),
      ]);
    return {
      users: { count: usersRes.count ?? 0, data: usersRes.data ?? [] },
      workflows: { count: workflowsRes.count ?? 0 },
      runs: { count: runsRes.count ?? 0 },
      bundles: { count: bundlesRes.count ?? 0 },
      rules: { count: rulesRes.count ?? 0 },
      azure: azureRes.data,
      hr: hrRes.data,
      subscription: subsRes.data,
    };
  },

  async tenantUsage(tenantId: string) {
    const [usersRes, workflowsRes, bundlesRes, rulesRes, runsRes] = await Promise.all([
      supabase.from('users').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId),
      supabase
        .from('workflow_definitions')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId),
      supabase
        .from('bundles')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId),
      supabase
        .from('jml_rules')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId),
      supabase
        .from('jml_runs')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId),
    ]);
    return {
      users: usersRes.count ?? 0,
      workflows: workflowsRes.count ?? 0,
      bundles: bundlesRes.count ?? 0,
      rules: rulesRes.count ?? 0,
      jml_runs: runsRes.count ?? 0,
    };
  },

  async assignPlan(tenantId: string, planId: string) {
    const { data, error } = await supabase
      .from('subscriptions')
      .upsert(
        {
          tenant_id: tenantId,
          plan_id: planId,
          status: 'active',
          billing_interval: 'monthly',
          current_period_start: nowIso(),
          current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          updated_at: nowIso(),
        },
        { onConflict: 'tenant_id' }
      )
      .select('*, billing_plans(*)')
      .single();
    return { data, error };
  },
};

// ─── M365 Groups (cached from Graph API sync) ─────────────

export const m365Groups = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('m365_groups')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('display_name');
    return { data: data ?? [], error };
  },
  async clearAll(tenantId: string) {
    const { error } = await supabase.from('m365_groups').delete().eq('tenant_id', tenantId);
    return { error };
  },
};

// ─── M365 Licenses (cached from Graph API sync) ───────────

export const m365Licenses = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('m365_licenses')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('sku_part_number');
    return { data: data ?? [], error };
  },
  async clearAll(tenantId: string) {
    const { error } = await supabase.from('m365_licenses').delete().eq('tenant_id', tenantId);
    return { error };
  },
};

// ─── User Templates (Slice 5a) ─────────────────────────────

export const userTemplates = {
  async list(tenantId: string) {
    const { data, error } = await supabase
      .from('user_templates')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('name', { ascending: true });
    return { data: data ?? [], error };
  },

  async get(id: string) {
    const { data, error } = await supabase.from('user_templates').select('*').eq('id', id).single();
    return { data, error };
  },

  async create(row: Insert<'user_templates'>) {
    const { data, error } = await supabase.from('user_templates').insert(row).select().single();
    return { data, error };
  },

  async update(id: string, updates: Update<'user_templates'>) {
    const { data, error } = await supabase
      .from('user_templates')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('user_templates').delete().eq('id', id);
    return { error };
  },

  /**
   * Atomically replace a template's group + license sets. Two delete-then-
   * insert calls; `delete` is a no-op when the set is empty.
   */
  async replaceLinks(templateId: string, groupIds: string[], licenseIds: string[]) {
    {
      const { error } = await supabase
        .from('user_template_groups')
        .delete()
        .eq('template_id', templateId);
      if (error) return { error };
    }
    if (groupIds.length > 0) {
      const rows = groupIds.map((m365_group_id) => ({
        template_id: templateId,
        m365_group_id,
      }));
      const { error } = await supabase.from('user_template_groups').insert(rows);
      if (error) return { error };
    }
    {
      const { error } = await supabase
        .from('user_template_licenses')
        .delete()
        .eq('template_id', templateId);
      if (error) return { error };
    }
    if (licenseIds.length > 0) {
      const rows = licenseIds.map((m365_license_id) => ({
        template_id: templateId,
        m365_license_id,
      }));
      const { error } = await supabase.from('user_template_licenses').insert(rows);
      if (error) return { error };
    }
    return { error: null };
  },

  /** Fetch a template's linked group ids. */
  async listGroups(templateId: string) {
    const { data, error } = await supabase
      .from('user_template_groups')
      .select('m365_group_id')
      .eq('template_id', templateId);
    return {
      data: (data ?? []).map((r) => r.m365_group_id),
      error,
    };
  },

  /** Fetch a template's linked license ids. */
  async listLicenses(templateId: string) {
    const { data, error } = await supabase
      .from('user_template_licenses')
      .select('m365_license_id')
      .eq('template_id', templateId);
    return {
      data: (data ?? []).map((r) => r.m365_license_id),
      error,
    };
  },
};

// ─── Aggregate export ──────────────────────────────────────

export const db = {
  workflows,
  jmlRules,
  jmlRuns,
  jmlSchedules,
  bundles,
  auditLogs,
  hrEmployees,
  m365Users,
  azureAdConfigs,
  hrProviderConfigs,
  roles,
  users,
  invitations,
  pendingRegistrations,
  tenants,
  tenantSettings,
  settings,
  billingPlans,
  subscriptions,
  leaverApprovalRequests,
  scheduledOffboardings,
  usageTracking,
  employmentTypes,
  userTypes,
  passwordResetTokens,
  adminQueries,
  m365Groups,
  m365Licenses,
  userTemplates,
};

export default db;

// Re-export helper types for convenience.
export type { Insert, Row, Update } from './db.types';
